import React from 'react';
import { motion } from 'framer-motion';
import { Plane, Globe, Users, Star, Heart, Gift, MapPin, Calendar, Shield, Award, ArrowRight, DollarSign, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const TRAVEL_CLUB_URL = "https://prestigioustravelclub.net/Video";

const TravelClubPage = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Plane,
      title: 'Exclusive Travel Deals',
      description: 'Access members-only pricing on flights, hotels, cruises, and vacation packages worldwide.',
    },
    {
      icon: Globe,
      title: 'Global Destinations',
      description: 'Explore over 1 million destinations with our extensive network of travel partners.',
    },
    {
      icon: Users,
      title: 'Concierge Service',
      description: 'Personal travel advisors with 40+ years of combined experience planning your perfect trip.',
    },
    {
      icon: Gift,
      title: 'Member Rewards',
      description: 'Earn points on every booking and redeem for free travel, upgrades, and exclusive experiences.',
    },
    {
      icon: Shield,
      title: 'Travel Protection',
      description: 'Comprehensive coverage options to protect your investment and give you peace of mind.',
    },
    {
      icon: Heart,
      title: 'Give Back Travel',
      description: 'A portion of every membership supports The Foundation\'s charitable initiatives.',
    },
  ];

  const experiences = [
    {
      title: 'Family Vacations',
      image: 'https://images.unsplash.com/photo-1602002418816-5c0aeef426aa?auto=format&fit=crop&w=800&q=80',
      description: 'Create lasting memories with curated family-friendly destinations and activities.',
    },
    {
      title: 'Luxury Escapes',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80',
      description: 'Indulge in five-star resorts, private villas, and exclusive experiences.',
    },
    {
      title: 'Adventure Travel',
      image: 'https://images.unsplash.com/photo-1530789253388-582c481c54b0?auto=format&fit=crop&w=800&q=80',
      description: 'From safaris to mountain treks, discover adventures that transform.',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="travel-club-page">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2000&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1120]/90 via-[#0B1120]/70 to-[#0B1120]/50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_noble-causes/artifacts/jwosv9l2_image.png"
                alt="Prestigious Travel Club Inc."
                className="h-28 sm:h-40 w-auto drop-shadow-2xl"
              />
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white tracking-tight leading-tight mb-6">
              Experience the Joy of
              <span className="block text-amber-400">Vacation Travel</span>
            </h1>
            
            <p className="text-xl text-white/80 font-light leading-relaxed mb-8">
              Prestigious Travel Club helps families create unforgettable memories with exclusive 
              access to the world's best destinations, unbeatable prices, and personalized service.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => window.open(TRAVEL_CLUB_URL, '_blank')}
                className="bg-amber-500 hover:bg-amber-400 text-[#0B1120] font-semibold px-8 py-6 text-lg rounded-full"
              >
                Explore Memberships
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-[#1E3A8A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: '1M+', label: 'Destinations' },
              { value: '40+', label: 'Years Experience' },
              { value: '50K+', label: 'Happy Travelers' },
              { value: '24/7', label: 'Member Support' },
            ].map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <p className="text-4xl font-bold font-serif mb-1">{stat.value}</p>
                <p className="text-white/70 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#1E3A8A] mb-4">
              Member Benefits
            </p>
            <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] mb-6">
              Why Join the <span className="text-[#1E3A8A]">Club</span>
            </h2>
            <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto">
              More than just travel—it's a lifestyle of exploration, savings, and making memories that last.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={benefit.title}
                className="p-8 rounded-2xl border border-slate-200 hover:border-[#1E3A8A]/30 hover:shadow-lg transition-all group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-[#DBEAFE] group-hover:bg-[#1E3A8A] flex items-center justify-center mb-6 transition-colors">
                  <benefit.icon className="w-7 h-7 text-[#1E3A8A] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3 font-serif">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 font-light">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experiences */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#1E3A8A] mb-4">
              Travel Your Way
            </p>
            <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] mb-6">
              Curated <span className="text-[#1E3A8A]">Experiences</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {experiences.map((exp, idx) => (
              <motion.div
                key={exp.title}
                className="group relative rounded-3xl overflow-hidden aspect-[4/5]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <img 
                  src={exp.image}
                  alt={exp.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120]/90 via-[#0B1120]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-semibold text-white mb-2 font-serif">{exp.title}</h3>
                  <p className="text-white/80 font-light">{exp.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Your Donation Unlocks */}
      <section className="py-24 bg-[#0B1120]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-400 mb-4">
              Your Travel Certificate Includes
            </p>
            <h2 className="text-4xl sm:text-5xl font-medium text-white tracking-tight font-serif">
              What Your Donation Unlocks
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Award,
                title: 'Premium Travel Certificate',
                description: 'Receive a certificate for luxury accommodations at stunning resort destinations.',
                highlight: true,
              },
              {
                icon: MapPin,
                title: 'Beautiful Locations',
                description: 'Choose from hand-selected properties in the most desirable destinations worldwide.',
                highlight: false,
              },
              {
                icon: Calendar,
                title: 'Flexible Redemption',
                description: 'Book on your schedule with generous redemption windows and easy booking.',
                highlight: false,
              },
              {
                icon: DollarSign,
                title: '100% Tax-Deductible',
                description: 'Your entire donation qualifies as a tax-deductible charitable contribution.',
                highlight: true,
              },
              {
                icon: Heart,
                title: 'Families Supported',
                description: 'Every certificate given means a family receives food, clothing, or housing assistance.',
                highlight: false,
              },
              {
                icon: Sparkles,
                title: 'Premium Experience',
                description: "Enjoy resort-level stays, concierge support, and a trip you'll never forget.",
                highlight: false,
              },
            ].map((item, idx) => (
              <motion.div
                key={item.title}
                className={`p-8 rounded-2xl border transition-all ${
                  item.highlight 
                    ? 'bg-amber-500/10 border-amber-500/30' 
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${
                  item.highlight ? 'bg-amber-500/20' : 'bg-white/10'
                }`}>
                  <item.icon className={`w-6 h-6 ${item.highlight ? 'text-amber-400' : 'text-amber-400/80'}`} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400 font-light">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Foundation Connection */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-600 mb-4">
                Travel + Purpose
              </p>
              <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] mb-6 leading-tight">
                Your Membership
                <span className="block text-[#1E3A8A]">Gives Back</span>
              </h2>
              <p className="text-lg text-slate-600 font-light mb-6 leading-relaxed">
                When you join Prestigious Travel Club, you're not just investing in unforgettable 
                experiences—you're supporting families in need. A portion of every membership 
                directly funds The Prestigious Travel Club Foundation.
              </p>
              <p className="text-lg text-slate-600 font-light mb-8 leading-relaxed">
                That means feeding families, supporting veterans, funding children's hospitals, 
                and empowering communities. Travel the world while making a difference at home.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-8 py-6 text-lg rounded-full"
              >
                Learn About The Foundation
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Heart, label: 'Families Fed', value: '12,500+' },
                  { icon: Shield, label: 'Veterans Helped', value: '850+' },
                  { icon: Star, label: 'Children Supported', value: '3,200+' },
                  { icon: Award, label: 'Mentorship Hours', value: '15K+' },
                ].map((stat, idx) => (
                  <div key={stat.label} className="bg-slate-50 rounded-2xl p-6 text-center">
                    <stat.icon className="w-8 h-8 text-[#1E3A8A] mx-auto mb-3" />
                    <p className="text-2xl font-bold text-[#1E3A8A] font-serif">{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0B1120] text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Plane className="w-16 h-16 mx-auto mb-6 text-amber-400" />
            <h2 className="text-4xl sm:text-5xl font-medium mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl text-slate-300 font-light mb-10 max-w-2xl mx-auto">
              Join thousands of families who travel smarter, save more, and give back 
              with every adventure.
            </p>
            <Button
              onClick={() => window.open(TRAVEL_CLUB_URL, '_blank')}
              className="bg-amber-500 hover:bg-amber-400 text-[#0B1120] font-semibold px-12 py-7 text-xl rounded-full"
            >
              Visit Prestigious Travel Club
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TravelClubPage;
